import type { ReaderPdfBookmarkAddStatus, StorageGateway, StoredReaderPdfBookmark, StoredReaderPdfBookmarks } from "@/interface/DTO_Interfaces"
import { getReaderPdfStorageKey } from "@/ts/reader-pdf-storage-key"

export const READER_PDF_BOOKMARKS_STORAGE_KEY = "informatics/pdf-bookmarks"

const isPositivePageNumber = (value: unknown): value is number => typeof value === "number" && Number.isInteger(value) && value > 0

const toStoredReaderPdfBookmark = (value: unknown): StoredReaderPdfBookmark | null => {
        if (!value || typeof value !== "object") {
                return null
        }

        const candidate = value as Partial<Record<keyof StoredReaderPdfBookmark, unknown>>
        const hasRequiredShape = (
                typeof candidate.id === "string" &&
                typeof candidate.bookId === "string" &&
                typeof candidate.pdfId === "string" &&
                isPositivePageNumber(candidate.pageNumber) &&
                typeof candidate.title === "string" &&
                typeof candidate.createdAt === "string"
        )

        if (!hasRequiredShape) {
                return null
        }

        return {
                id: candidate.id as string,
                bookId: candidate.bookId as string,
                pdfId: candidate.pdfId as string,
                pageNumber: candidate.pageNumber as number,
                pageLabel: typeof candidate.pageLabel === "string" ? candidate.pageLabel : undefined,
                title: candidate.title as string,
                createdAt: candidate.createdAt as string,
        }
}

const isStoredReaderPdfBookmarks = (value: unknown): value is StoredReaderPdfBookmarks => {
        if (!value || typeof value !== "object") {
                return false
        }

        const candidate = value as {
                bookId?: unknown
                pdfId?: unknown
                items?: unknown
        }

        return typeof candidate.bookId === "string" && typeof candidate.pdfId === "string" && Array.isArray(candidate.items)
}

const sanitizeBookmarks = (items: unknown[]): StoredReaderPdfBookmark[] => {
        const bookmarks: StoredReaderPdfBookmark[] = []
        const bookmarkIds = new Set<string>()

        for (const item of items) {
                const bookmark = toStoredReaderPdfBookmark(item)

                if (!bookmark || bookmarkIds.has(bookmark.id)) {
                        continue
                }

                bookmarkIds.add(bookmark.id)
                bookmarks.push(bookmark)
        }

        return bookmarks
}

const createEmptyBookmarks = (bookId: string, pdfId: string): StoredReaderPdfBookmarks => ({
        bookId,
        pdfId,
        items: [],
})

export const createReaderPdfBookmarkId = (options: { bookId: string; pdfId: string; pageNumber: number }) =>
        `${options.bookId}/${options.pdfId}/page-${options.pageNumber}`

export const readStoredReaderPdfBookmarks = (gateway: StorageGateway, options: { bookId: string; pdfId: string } = { bookId: "informatika", pdfId: "informatic" }): StoredReaderPdfBookmarks | null => {
        const storedBookmarks = gateway.getItem<unknown>(getReaderPdfStorageKey(READER_PDF_BOOKMARKS_STORAGE_KEY, options))

        if (!isStoredReaderPdfBookmarks(storedBookmarks)) {
                return null
        }

        return {
                bookId: storedBookmarks.bookId,
                pdfId: storedBookmarks.pdfId,
                items: sanitizeBookmarks(storedBookmarks.items),
        }
}

export const hydrateReaderPdfBookmarks = (gateway: StorageGateway, options: { bookId: string; pdfId: string }): StoredReaderPdfBookmarks => {
        const storedBookmarks = readStoredReaderPdfBookmarks(gateway, options)

        if (!storedBookmarks || storedBookmarks.bookId !== options.bookId || storedBookmarks.pdfId !== options.pdfId) {
                return createEmptyBookmarks(options.bookId, options.pdfId)
        }

        return storedBookmarks
}

export const addReaderPdfBookmark = (
        gateway: StorageGateway,
        bookmark: Omit<StoredReaderPdfBookmark, "id"> & { id?: string }
): { status: ReaderPdfBookmarkAddStatus; bookmarks: StoredReaderPdfBookmarks } => {
        const currentBookmarks = hydrateReaderPdfBookmarks(gateway, {
                bookId: bookmark.bookId,
                pdfId: bookmark.pdfId,
        })
        const bookmarkId = bookmark.id ?? createReaderPdfBookmarkId(bookmark)

        if (currentBookmarks.items.some((item) => item.id === bookmarkId || item.pageNumber === bookmark.pageNumber)) {
                return {
                        status: "exists",
                        bookmarks: currentBookmarks,
                }
        }

        const nextBookmarks: StoredReaderPdfBookmarks = {
                bookId: bookmark.bookId,
                pdfId: bookmark.pdfId,
                items: [
                        ...currentBookmarks.items,
                        {
                                ...bookmark,
                                id: bookmarkId,
                        },
                ],
        }

        gateway.setItem(getReaderPdfStorageKey(READER_PDF_BOOKMARKS_STORAGE_KEY, bookmark), nextBookmarks)

        return {
                status: "created",
                bookmarks: nextBookmarks,
        }
}

export const removeReaderPdfBookmark = (
        gateway: StorageGateway,
        options: { bookId: string; pdfId: string; pageNumber: number }
): StoredReaderPdfBookmarks => {
        const currentBookmarks = hydrateReaderPdfBookmarks(gateway, options)
        const nextBookmarks: StoredReaderPdfBookmarks = {
                ...currentBookmarks,
                items: currentBookmarks.items.filter((bookmark) => bookmark.pageNumber !== options.pageNumber),
        }

        gateway.setItem(getReaderPdfStorageKey(READER_PDF_BOOKMARKS_STORAGE_KEY, options), nextBookmarks)

        return nextBookmarks
}
